import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search, User, Users, Briefcase, Globe, X, Send, BookOpen, Clock, CheckCircle2, ChevronLeft } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../../common/apiClient";
import SummaryApi from "../../../common/SummaryApi";
import SelectDropdown from "../../../customStyles/SelectDropdown";
import CustomDatePicker from "../../../customStyles/CustomDatePicker";
import PageLoader from "../../../helpers/PageLoader";

const AssignLmsCourse = () => {
  const { portalName } = useParams();
  const navigate = useNavigate();

  // Loading States
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Data States
  const [employees, setEmployees] = useState([]);
  const [courses, setCourses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [branches, setBranches] = useState([]);

  // Assignment States
  const [audienceType, setAudienceType] = useState("Employees"); // Employees, Department, Designation, All
  const [selectedAudience, setSelectedAudience] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [courseSearch, setCourseSearch] = useState("");

  // Assignment Settings
  const [settings, setAssignmentSettings] = useState({
    dueDate: "",
    priority: "Medium",
    allowRetake: "Yes",
    sendNotification: "Yes"
  });

  // Fetch all necessary data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [empRes, courseRes, deptRes, desigRes, branchRes] = await Promise.all([
        api({ ...SummaryApi.getAllEmployees }),
        api({ ...SummaryApi.getAllLmsCourses }),
        api({ ...SummaryApi.getAllDepartments }),
        api({ ...SummaryApi.getAllDesignations }),
        api({ ...SummaryApi.getAllDivisions }) // Branches/Divisions
      ]);

      if (empRes.data.success) setEmployees(empRes.data.data);
      if (courseRes.data.success) setCourses(courseRes.data.data.courses || []);
      if (deptRes.data.success) setDepartments(deptRes.data.data.map(d => ({ value: d._id, label: d.departmentName })));
      if (desigRes.data.success) setDesignations(desigRes.data.data.map(d => ({ value: d._id, label: d.designationName })));
      if (branchRes.data.success) setBranches(branchRes.data.data.map(b => ({ value: b._id, label: b.divisionName })));
      
    } catch (error) {
      toast.error("Failed to load required data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Handlers
  const handleToggleAudience = (item) => {
    const id = item._id || item.value;
    setSelectedAudience(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleAssign = async () => {
    if (!selectedCourse) return toast.warning("Please select a course");
    if (selectedAudience.length === 0 && audienceType !== "All") return toast.warning("Please select audience");
    if (!settings.dueDate) return toast.warning("Please select a due date");

    try {
      setSubmitting(true);
      const payload = {
        lmsCourseId: selectedCourse._id,
        audienceType: audienceType,
        assignedTo: selectedAudience, // IDs of employees, departments, or designations
        dueDate: settings.dueDate,
        priority: settings.priority,
        allowRetake: settings.allowRetake === "Yes",
        sendNotification: settings.sendNotification === "Yes"
      };

      const response = await api({
        ...SummaryApi.createTaggedLms,
        data: payload
      });

      if (response.data.success) {
        toast.success("Course assigned successfully");
        navigate(`/${portalName}/hr/lms-management/tagged-courses`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to assign course");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="bg-gray-50 min-h-screen pb-20">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full transition"><ChevronLeft size={20}/></button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Assign Course</h1>
            <p className="text-xs text-gray-500">Select audience and assign a course from available modules</p>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto p-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Main Content (Left) */}
        <div className="lg:col-span-9 space-y-6">
          
          {/* 1. Choose Audience */}
          <section className="bg-white p-6 rounded-2xl border shadow-sm">
            <h2 className="text-md font-bold mb-4 flex items-center gap-2">1. Choose Audience</h2>
            
            {/* Audience Type Selection */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { id: "Employees", label: "Employees", icon: <User size={20}/>, desc: "Assign to individuals" },
                { id: "Department", label: "Department", icon: <Users size={20}/>, desc: "Assign to departments" },
                { id: "Designation", label: "Designation", icon: <Briefcase size={20}/>, desc: "Assign to roles" },
                { id: "All", label: "All Employees", icon: <Globe size={20}/>, desc: "Assign to everyone" }
              ].map((type) => (
                <button
                  key={type.id}
                  onClick={() => { setAudienceType(type.id); setSelectedAudience([]); }}
                  className={`p-4 border-2 rounded-xl text-left transition-all ${audienceType === type.id ? "border-red-600 bg-red-50" : "border-gray-100 hover:border-gray-200 bg-white"}`}
                >
                  <div className={`p-2 rounded-lg w-fit mb-3 ${audienceType === type.id ? "bg-red-600 text-white" : "bg-gray-100 text-gray-500"}`}>
                    {type.icon}
                  </div>
                  <p className="text-sm font-bold text-gray-900">{type.label}</p>
                  <p className="text-[10px] text-gray-500">{type.desc}</p>
                </button>
              ))}
            </div>

            {/* Filters (only visible if not 'All') */}
            {audienceType !== "All" && (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6 bg-gray-50 p-4 rounded-xl border border-gray-100">
                <div className="md:col-span-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16}/>
                  <input 
                    placeholder="Search name, ID..." 
                    className="w-full pl-9 pr-4 py-2 border rounded-lg text-xs outline-none focus:ring-1 focus:ring-red-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <SelectDropdown options={branches} placeholder="All Branches" ChildClassName="h-[34px] text-xs" />
                <SelectDropdown options={departments} placeholder="All Departments" ChildClassName="h-[34px] text-xs" />
                <SelectDropdown options={designations} placeholder="All Designations" ChildClassName="h-[34px] text-xs" />
                <SelectDropdown options={[{id:"Active",label:"Active"}]} placeholder="Status: Active" ChildClassName="h-[34px] text-xs" />
              </div>
            )}

            {/* Selection Area (Employees/Depts/Desigs) */}
            {audienceType === "Employees" && (
                <div className="flex flex-wrap gap-2">
                    {employees.filter(e => e.firstName.toLowerCase().includes(searchTerm.toLowerCase())).slice(0, 15).map(emp => (
                        <div 
                            key={emp._id}
                            onClick={() => handleToggleAudience(emp)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-all text-xs font-medium ${selectedAudience.includes(emp._id) ? "bg-red-600 border-red-600 text-white shadow-md" : "bg-white text-gray-600 border-gray-200 hover:border-red-300"}`}
                        >
                            <span className="w-5 h-5 rounded-full bg-gray-200 text-[10px] flex items-center justify-center text-gray-700">
                                {emp.firstName[0]}
                            </span>
                            {emp.firstName} {emp.lastName}
                            {selectedAudience.includes(emp._id) && <X size={12} className="ml-1"/>}
                        </div>
                    ))}
                    <button className="text-red-600 text-xs font-bold px-3">+ {employees.length - 15} more</button>
                </div>
            )}
          </section>

          {/* 2. Choose Course */}
          <section className="bg-white p-6 rounded-2xl border shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <h2 className="text-md font-bold flex items-center gap-2">2. Choose Course</h2>
                <div className="relative w-full md:w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16}/>
                    <input 
                        value={courseSearch}
                        onChange={(e) => setCourseSearch(e.target.value)}
                        placeholder="Search courses..." 
                        className="w-full pl-9 pr-4 py-2 border rounded-xl text-xs outline-none focus:ring-1 focus:ring-red-500" 
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {courses.filter(c => c.courseTitle.toLowerCase().includes(courseSearch.toLowerCase())).map((course) => (
                <div 
                  key={course._id}
                  onClick={() => setSelectedCourse(course)}
                  className={`relative group cursor-pointer border-2 rounded-2xl overflow-hidden transition-all h-full flex flex-col ${selectedCourse?._id === course._id ? "border-red-600 ring-4 ring-red-50 shadow-lg" : "border-gray-100 hover:border-gray-200"}`}
                >
                    <div className="relative h-32">
                        <img src={course.thumbnail?.url || "https://via.placeholder.com/400x200"} className="w-full h-full object-cover" alt="course"/>
                        <div className="absolute top-2 left-2 bg-red-600 text-white text-[9px] px-2 py-0.5 rounded font-bold uppercase">Mandatory</div>
                        {selectedCourse?._id === course._id && (
                            <div className="absolute top-2 right-2 bg-white text-red-600 rounded-full p-0.5"><CheckCircle2 size={20}/></div>
                        )}
                    </div>
                    <div className="p-3 flex-1 flex flex-col">
                        <h3 className="text-xs font-bold text-gray-900 line-clamp-1 mb-1">{course.courseTitle}</h3>
                        <p className="text-[10px] text-gray-400 font-bold uppercase mb-3">{course.category}</p>
                        <div className="mt-auto flex items-center gap-3 text-[10px] text-gray-500 font-medium">
                            <span className="flex items-center gap-1"><BookOpen size={12}/> {course.uploadContent?.length || 0} Lessons</span>
                            <span className="flex items-center gap-1"><Clock size={12}/> {course.duration}</span>
                        </div>
                    </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Sidebar (Right) */}
        <div className="lg:col-span-3 space-y-6">
          <section className="bg-white p-5 rounded-2xl border shadow-sm sticky top-24">
            <h3 className="text-sm font-bold text-gray-900 mb-6">Assignment Summary</h3>
            
            {/* Audience Summary */}
            <div className="mb-6">
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Audience</p>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="p-2 bg-red-100 text-red-600 rounded-lg"><Users size={18}/></div>
                    <div>
                        <p className="text-xs font-bold text-gray-900">{audienceType === "All" ? "Everyone" : `${selectedAudience.length} selected`}</p>
                        <button className="text-[10px] text-red-600 font-bold hover:underline">View List</button>
                    </div>
                </div>
            </div>

            {/* Course Summary */}
            <div className="mb-6">
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Selected Course</p>
                {selectedCourse ? (
                    <div className="p-3 border border-red-100 bg-red-50/30 rounded-xl">
                        <p className="text-xs font-bold text-gray-900 mb-1">{selectedCourse.courseTitle}</p>
                        <div className="flex items-center gap-2 text-[10px] text-gray-500">
                            <span>{selectedCourse.category}</span> • <span>{selectedCourse.duration}</span>
                        </div>
                    </div>
                ) : (
                    <div className="p-4 border-2 border-dashed border-gray-100 rounded-xl text-center">
                        <p className="text-[10px] text-gray-400 font-medium italic">No course selected</p>
                    </div>
                )}
            </div>

            {/* Settings */}
            <div className="space-y-4 pt-4 border-t">
                <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Due Date</label>
                    <CustomDatePicker 
                        value={settings.dueDate}
                        onChange={(date) => setAssignmentSettings({...settings, dueDate: date})}
                    />
                </div>
                <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Priority</label>
                    <SelectDropdown 
                        options={[{id:"High",label:"High"},{id:"Medium",label:"Medium"},{id:"Low",label:"Low"}]}
                        value={settings.priority}
                        onChange={(val) => setAssignmentSettings({...settings, priority: val})}
                        ChildClassName="h-[36px] text-xs"
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Retake</label>
                        <SelectDropdown options={[{id:"Yes",label:"Yes"},{id:"No",label:"No"}]} value={settings.allowRetake} onChange={(val) => setAssignmentSettings({...settings, allowRetake: val})} ChildClassName="h-[36px] text-xs"/>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Notify</label>
                        <SelectDropdown options={[{id:"Yes",label:"Yes"},{id:"No",label:"No"}]} value={settings.sendNotification} onChange={(val) => setAssignmentSettings({...settings, sendNotification: val})} ChildClassName="h-[36px] text-xs"/>
                    </div>
                </div>
            </div>

            <div className="mt-8 pt-4 border-t flex items-center justify-between">
                <span className="text-xs font-bold text-gray-500">Total Learners</span>
                <span className="text-xl font-black text-gray-900">{audienceType === "All" ? employees.length : selectedAudience.length}</span>
            </div>
          </section>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t p-4 px-6 z-30 flex justify-end gap-3 shadow-[0_-4px_15px_rgba(0,0,0,0.05)]">
        <button onClick={() => navigate(-1)} className="px-6 py-2 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-all">Cancel</button>
        <button 
          disabled={submitting}
          onClick={handleAssign}
          className="px-8 py-2 bg-red-600 text-white text-sm font-bold rounded-xl flex items-center gap-2 hover:bg-red-700 shadow-lg shadow-red-100 transition-all disabled:opacity-50"
        >
          {submitting ? "Assigning..." : <>Assign Course <Send size={16}/></>}
        </button>
      </div>
    </div>
  );
};

export default AssignLmsCourse;